import { Articulation } from 'types/articulation';
import { FormSchema } from 'types/form-schema';
import { SourceInstitution } from 'types/source-institution';
import { extractSourceInstitutionID } from 'utils/required-credentials';
import { getTotalCreditPoints as getArrangementCP } from 'utils/arrangement';
import { getHeadingAndSubHeading } from 'utils/required-credential';
import { safeFilter } from 'utils/common';

/**
 * Given a search term, returns a list of matching Articulations.
 * @export
 * @param {Articulation[]} articulations
 * @param {SourceInstitution} sourceInstitution
 * @param {string} searchTerm
 * @return {*}  {Articulation[]}
 */
export function getFilteredArticulations(
  articulations: Articulation[],
  sourceInstitution: SourceInstitution,
  formSchema: FormSchema,
  searchTerm: string,
): Articulation[] {
  const searchTermTransformed = searchTerm.trim().toLowerCase();
  const filteredArticulations = articulations.filter((articulation) => {
    const sourceInstitutions = articulation.requiredCredentials.map(extractSourceInstitutionID);
    if (!sourceInstitutions.includes(sourceInstitution.ID)) {
      return false;
    }
    const sourceMatched = articulation.arrangements.some((arrangement) => {
      const sourceSubjectMatched = articulation.requiredCredentials.some((credential) => {
        const { headings, subHeadings } = formSchema[credential.type];
        return credential.matchingCriteria.some(
          (match) =>
            [...headings, ...subHeadings].includes(match.field) &&
            match.value.trim().toLowerCase().includes(searchTermTransformed),
        );
      });
      const targetSubjectMatched = arrangement.subjects.some((subject) => {
        return (
          subject.name.toLowerCase().includes(searchTermTransformed) ||
          subject.code.toLowerCase().includes(searchTermTransformed)
        );
      });
      return sourceSubjectMatched || targetSubjectMatched;
    });
    const targetMatched =
      articulation.course.name.includes(searchTermTransformed) ||
      articulation.course.ID.includes(searchTermTransformed) ||
      articulation.course.cricosCodes.some((code) => code.toLowerCase().includes(searchTermTransformed));
    return sourceMatched || targetMatched;
  });
  return filteredArticulations;
}

/**
 * Groups Articulations by its Credential Source Institution IDs.
 * @export
 * @param {Articulation[]} articulations
 * @return {*}  {Map<string, Articulation[]>}
 */
export function groupedBySourceInstitutionIDs(articulations: Articulation[]): Map<string, Articulation[]> {
  return articulations.reduce<Map<string, Articulation[]>>((map, articulation, index, array) => {
    const sourceInstitutionIDs = safeFilter(articulation.requiredCredentials.map(extractSourceInstitutionID));
    const key = sourceInstitutionIDs.sort().join(', ');
    const list = map.get(key) ?? [];
    list.push(articulation);
    if (index === array.length - 1) {
      map.set(key, sortArticulationItemBlocks(list));
    } else {
      map.set(key, list);
    }
    return map;
  }, new Map());
}

/**
 * Groups Articulations by its Credential Source Institution IDs.
 * @export
 * @param {Articulation[]} articulations
 * @return {*}  {Map<string, Articulation[]>}
 */
export function groupedByRequiredCredentials(articulations: Articulation[]): Map<string, Articulation[]> {
  return articulations.reduce<Map<string, Articulation[]>>((map, articulation, index, array) => {
    const sourceInstitutionCredentials = safeFilter(articulation.requiredCredentials.map((cred) => cred.ID));
    const key = sourceInstitutionCredentials.sort().join(', ');
    const list = map.get(key) ?? [];
    list.push(articulation);
    if (index === array.length - 1) {
      map.set(key, sortArticulationItemBlocks(list));
    } else {
      map.set(key, list);
    }
    return map;
  }, new Map());
}

/**
 * Sorts Articulations by guaranteed entry (desc) > credit points (desc) > course name (asc).
 * Does not mutate input array, returns a new array.
 * @export
 * @param {Articulation[]} articulations
 * @return {*}  {Articulation[]}
 */
export function sortArticulationItemBlocks(articulations: Articulation[]): Articulation[] {
  return [...articulations].sort((a, b) => {
    const aGuaranteedEntry = a.arrangements.some((arrangement) => arrangement.guaranteedEntryOnly);
    const bGuaranteedEntry = b.arrangements.some((arrangement) => arrangement.guaranteedEntryOnly);
    if (aGuaranteedEntry === bGuaranteedEntry) {
      const [aCP, bCP] = [getTotalCreditPoints(a), getTotalCreditPoints(b)];
      if (aCP === bCP) {
        return a.course.name.toLowerCase().localeCompare(b.course.name.toLowerCase());
      }
      return aCP > bCP ? -1 : 1;
    }
    return aGuaranteedEntry ? -1 : 1;
  });
}

/**
 * Sorts Articulations by guaranteed entry (desc) > credit points (desc) >
 * credential unit name (asc) > course name (asc).
 * Does not mutate input array, returns a new array.
 * @export
 * @param {Articulation[]} articulations
 * @param {FormSchema} formSchema
 * @return {*}  {Articulation[]}
 */
export function sortInitialArticulations(articulations: Articulation[], formSchema: FormSchema): Articulation[] {
  return [...articulations].sort((a, b) => {
    const aGuaranteedEntry = a.arrangements.some((arrangement) => arrangement.guaranteedEntryOnly);
    const bGuaranteedEntry = b.arrangements.some((arrangement) => arrangement.guaranteedEntryOnly);
    if (aGuaranteedEntry === bGuaranteedEntry) {
      const [aCP, bCP] = [getTotalCreditPoints(a), getTotalCreditPoints(b)];
      if (aCP === bCP) {
        const aHeadings = a.requiredCredentials
          .map((credential) => {
            const { heading } = getHeadingAndSubHeading(credential, formSchema);
            return heading;
          })
          .join(', ');
        const bHeadings = b.requiredCredentials
          .map((credential) => {
            const { heading } = getHeadingAndSubHeading(credential, formSchema);
            return heading;
          })
          .join(', ');
        if (aHeadings === bHeadings) {
          return a.course.name.toLowerCase().localeCompare(b.course.name.toLowerCase());
        }
        return aHeadings.localeCompare(bHeadings);
      }
      return aCP > bCP ? -1 : 1;
    }
    return aGuaranteedEntry ? -1 : 1;
  });
}

// make return the highest credit point total of arrangements

/**
 * Calculates the total credits points for an Articulation.
 * @export
 * @param {Articulation} articulation
 * @return {*}  {number}
 */
export function getTotalCreditPoints(articulation: Articulation): number {
  return articulation.arrangements.reduce<number>(
    (prev, arrangement) => (prev > getArrangementCP(arrangement) ? prev : getArrangementCP(arrangement)),
    0,
  );
}
